import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { AppSettingRow, AppWithStats, SettingsResponse } from '@/core/types';
import { api } from '@/lib/api';

type Props = {
  settings: SettingsResponse;
  apps: AppWithStats[];
};

const DEFAULTS = {
  session_length: 15,
  archive_threshold_days: 90,
  review_session_length: 10,
  archive_review_session_length: 10,
  unarchive_failure_threshold: 2,
};

export function AppsSection({ settings, apps }: Props) {
  const activeApps = apps.filter((a) => a.status === 'active');
  const initial: AppSettingRow[] = activeApps.map((a) => {
    const existing = settings.apps.find((s) => s.app_id === a.id);
    return existing ?? { app_id: a.id, ...DEFAULTS };
  });

  const [rows, setRows] = useState<AppSettingRow[]>(initial);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: api.saveAppSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('App settings saved');
    },
    onError: (err: Error) => toast.error('Failed to save', { description: err.message }),
  });

  const update = (appId: string, patch: Partial<AppSettingRow>) =>
    setRows((rs) => rs.map((r) => (r.app_id === appId ? { ...r, ...patch } : r)));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ apps: rows });
      }}
      className="flex flex-col gap-6"
    >
      {rows.map((row) => {
        const app = activeApps.find((a) => a.id === row.app_id);
        return (
          <section
            key={row.app_id}
            className="border-border bg-card flex flex-col gap-3 rounded-md border p-4"
          >
            <h3 className="font-display text-lg font-semibold">
              {app?.display_name ?? row.app_id}
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <NumberField
                label="Session length"
                hint="Practice mode cards per session"
                value={row.session_length}
                onChange={(v) => update(row.app_id, { session_length: v })}
                min={1}
                max={100}
              />
              <NumberField
                label="Archive threshold (days)"
                hint="Move memorized cards to archive after this many days"
                value={row.archive_threshold_days}
                onChange={(v) => update(row.app_id, { archive_threshold_days: v })}
                min={1}
                max={365}
              />
              <NumberField
                label="Review session length"
                value={row.review_session_length}
                onChange={(v) => update(row.app_id, { review_session_length: v })}
                min={1}
                max={100}
              />
              <NumberField
                label="Archive review session length"
                value={row.archive_review_session_length}
                onChange={(v) => update(row.app_id, { archive_review_session_length: v })}
                min={1}
                max={100}
              />
              <NumberField
                label="Unarchive failure threshold"
                hint="Bring back to active after this many archive review failures"
                value={row.unarchive_failure_threshold}
                onChange={(v) => update(row.app_id, { unarchive_failure_threshold: v })}
                min={1}
                max={10}
              />
            </div>
          </section>
        );
      })}

      <div>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function NumberField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))}
        className="h-9"
      />
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}
